import type { Player, Role, User } from './types';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';
const TOKEN_KEY = 'bbc.token';

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(t: string | null) {
  try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch {}
}

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function json<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try { const j = (await res.json()) as { error?: string }; if (j?.error) msg = j.error; } catch {}
    throw new ApiError(res.status, msg);
  }
  return res.json() as Promise<T>;
}

const post = <T>(path: string, body: unknown) => json<T>(path, { method: 'POST', body: JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => json<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

// auth
export const login = (username: string, password: string) => post<{ token: string; user: User }>('/api/auth/login', { username, password });
export const me = () => json<{ user: User }>('/api/auth/me');
export const changePassword = (currentPassword: string, newPassword: string) =>
  post<{ user: User }>('/api/auth/password', { currentPassword, newPassword });

export const forgotPassword = (identifier: string) => post<{ ok: true }>('/api/auth/forgot', { identifier });
export const resetPassword = (token: string, newPassword: string) => post<{ token: string; user: User }>('/api/auth/reset', { token, newPassword });

// players
export const getPlayers = () => json<{ players: Player[] }>('/api/players');

// admin
export const adminListUsers = () => json<{ users: User[] }>('/api/admin/users');
export const adminCreateUser = (input: { username: string; email?: string; displayName?: string; role: Role }) =>
  post<{ user: User; tempPassword: string }>('/api/admin/users', input);
export const adminUpdateUser = (id: string, input: { displayName?: string; email?: string | null; role?: Role; active?: boolean; handicap?: number }) =>
  patch<{ user: User }>(`/api/admin/users/${encodeURIComponent(id)}`, input);
export const adminResetPassword = (id: string) => post<{ tempPassword: string }>(`/api/admin/users/${encodeURIComponent(id)}/reset-password`, {});

// league
import type { HomeData, RankingRow, TournamentDetail, TournamentInput, TournamentListItem, EntryStatus } from './types';
const del = <T>(path: string) => json<T>(path, { method: 'DELETE' });

export const getHome = () => json<HomeData>('/api/home');
export const getRankings = () => json<{ rankings: RankingRow[]; median: number }>('/api/rankings');
export const getTournaments = () => json<{ tournaments: TournamentListItem[] }>('/api/tournaments');
export const getTournament = (id: string) => json<TournamentDetail>(`/api/tournaments/${id}`);
export const createTournament = (input: TournamentInput) => post<{ tournament: TournamentListItem }>('/api/tournaments', input);
export const updateTournament = (id: string, input: Partial<TournamentInput>) => patch<{ tournament: TournamentListItem }>(`/api/tournaments/${id}`, input);
export const deleteTournament = (id: string) => del<{ ok: true }>(`/api/tournaments/${id}`);
export const joinTournament = (id: string) => post<{ status: EntryStatus }>(`/api/tournaments/${id}/join`, {});
export const leaveTournament = (id: string) => post<{ ok: true }>(`/api/tournaments/${id}/leave`, {});
export const addEntry = (id: string, userId: string) => post<{ status: EntryStatus }>(`/api/tournaments/${id}/entries`, { userId });
export const removeEntry = (id: string, userId: string) => del<{ ok: true }>(`/api/tournaments/${id}/entries/${userId}`);
export const startTournament = (id: string) => post<{ ok: true }>(`/api/tournaments/${id}/start`, {});
export const nextRound = (id: string) => post<{ ok: true; round: number }>(`/api/tournaments/${id}/rounds/next`, {});
export const finishTournament = (id: string) => post<{ ok: true }>(`/api/tournaments/${id}/finish`, {});
export const reopenTournament = (id: string) => post<{ ok: true }>(`/api/tournaments/${id}/reopen`, {});
export const enterScore = (matchId: string, scoreA: number, scoreB: number) => post<{ ok: true }>(`/api/matches/${matchId}/score`, { scoreA, scoreB });
export const clearScore = (matchId: string) => post<{ ok: true }>(`/api/matches/${matchId}/score`, { clear: true });
